"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TaxSolutions() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const services = [{
    number: "N° 001",
    title: "INDIVIDUAL TAX COMPLIANCE",
    description: "Precision-focused tax preparation for individuals seeking accuracy and peace of mind. We navigate complex filing requirements to ensure every deduction is optimized while maintaining strict ethical standards.",
    features: ["Comprehensive annual tax filing", "Strategic deduction optimization", "Year-round advisory support", "Proactive risk management"],
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/individual_tax_planning.webp",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/individual_tax_planning.webp-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/individual_tax_planning.webp-wvc-sizes",
      alt: "Professional reviewing individual tax documents"
    },
    imageLeft: true
  }, {
    number: "N° 002",
    title: "BUSINESS FINANCIAL STRATEGY",
    description: "Strategic financial planning designed for growing businesses. We provide the clarity needed to manage regulatory compliance, optimize cash flow, and build a foundation for long-term financial stability.",
    features: ["Corporate tax planning and filing", "Regulatory compliance oversight", "Financial health assessments", "Proactive tax liability reduction"],
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/business_financial_strategy.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/business_financial_strategy.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/business_financial_strategy.png-wvc-sizes",
      alt: "Business professional analyzing financial data"
    },
    imageLeft: false
  }, {
    number: "N° 003",
    title: "PROACTIVE ADVISORY SERVICES",
    description: "Dedicated guidance for clients who value transparency and responsiveness. Our certified professionals offer personalized attention to help you navigate life's financial transitions with confidence.",
    features: ["Personalized financial roadmap", "Tax-efficient wealth planning", "Regulatory update monitoring", "Responsive client communication"],
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/proactive_tax_advisory.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/proactive_tax_advisory.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/proactive_tax_advisory.png-wvc-sizes",
      alt: "Consultant providing tax advisory services"
    },
    imageLeft: true
  }];
  return <section data-section-id="3627" id="tax-solutions-for-every-stage" className="bg-background text-foreground py-32 md:py-[200px] px-6 md:px-12 overflow-hidden">
      <div ref={header.ref} className={`max-w-[1400px] mx-auto mb-24 md:mb-32 transition-all duration-1000 ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="font-mono text-xs tracking-widest text-primary mb-8 md:mb-12">
          01 / SERVICES
        </div>
        <h2 className="font-default font-bold uppercase leading-[0.95] tracking-tight text-foreground">
          <span className="block text-[clamp(3rem,8vw,8rem)]">TAX SOLUTIONS</span>
          <span className="block text-[clamp(3rem,8vw,8rem)] md:pl-[200px]">
            FOR EVERY <i>STAGE</i>
          </span>
        </h2>
        <div className="mt-10 md:mt-14 md:pl-[200px]">
          <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground max-w-[560px]">
            Lovejoy Company provides tax services tailored to individuals and businesses seeking accurate compliance and strategic financial planning. We guide you through complex requirements with clarity and precision.
          </p>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto flex flex-col">
        {services.map((service, i) => {
        const card = useRevealOnScroll<HTMLDivElement>({
          threshold: 0.15
        });
        return <div data-index={i} key={i} className="flex flex-col">
              <div ref={card.ref} className={`grid grid-cols-1 lg:grid-cols-2 lg:min-h-[480px] shadow-md transition-all duration-1000 ${card.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
                <div className={`relative h-[320px] lg:h-auto overflow-hidden ${service.imageLeft ? "lg:order-1" : "lg:order-2"}`}>
                  <img src={service.image.src} data-wvc-srcset={service.image.srcset} data-wvc-sizes={service.image.sizes} alt={service.image.alt} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
                </div>

                <div className={`relative bg-card border border-border p-8 md:p-12 lg:p-14 flex flex-col justify-between ${service.imageLeft ? "lg:order-2" : "lg:order-1"}`}>
                  <div>
                    <div className="font-mono text-xs tracking-widest text-primary mb-6">
                      {service.number}
                    </div>
                    <h3 className="font-default font-bold uppercase leading-[1] tracking-tight text-foreground text-3xl md:text-4xl lg:text-5xl mb-8">
                      {service.title}
                    </h3>
                    <div className="h-px w-16 bg-border mb-8" />
                    <p className="font-default text-base leading-relaxed text-muted-foreground mb-8">
                      {service.description}
                    </p>
                    <ul className="space-y-3 mb-10">
                      {service.features.map((feature, j) => <li data-index={j} key={j} className="flex items-start gap-3 font-default text-sm uppercase tracking-wide text-foreground">
                          <span className="inline-block size-1.5 bg-accent mt-2 shrink-0" />
                          <span>{feature}</span>
                        </li>)}
                    </ul>
                  </div>

                  <div>
                    <Button asChild className="font-default uppercase tracking-widest text-xs h-12 px-6 bg-accent text-accent-foreground hover:bg-accent/90 transition-colors">
                      <Link to="#">
                        Get Started
                        <ArrowRight className="ml-2 size-4" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>

              {i < services.length - 1 && <div className="py-10 md:py-20 flex justify-end">
                  <div className="h-px w-1/2 md:w-1/3 bg-border" />
                </div>}
            </div>;
      })}
      </div>
    </section>;
}